import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { Button, Modal } from '@opendate-app/setlist';
import V3PerformerPicker from './V3PerformerPicker';
import { ARTIST_PICKER_MODAL_STYLE } from '../../shared/components/artistPickerModalStyle';
import '../styles/copyOfferModal.css';

const pickedArtistName = (pick) => pick.artist?.name || pick.newArtistName;

const CopyOfferModal = ({
  isOpen, isCopying, offer, confirm, team, onClose, onCopy,
}) => {
  const [pickedArtist, setPickedArtist] = useState(null);
  const [picking, setPicking] = useState(false);
  const openerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    openerRef.current = document.activeElement;
    setPickedArtist(null);
    setPicking(false);
    return () => { openerRef.current?.focus?.(); };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentPerformer = offer.primary_performer;

  const handlePick = (pick) => {
    const pickedCurrentArtist = pick.artistId && pick.artistId === currentPerformer?.artist_id;
    setPickedArtist(pickedCurrentArtist ? null : pick);
    setPicking(false);
  };

  if (picking) {
    const closePicker = () => setPicking(false);
    return (
      <Popup
        open
        onClose={closePicker}
        modal
        contentStyle={ARTIST_PICKER_MODAL_STYLE}
        className="offer-modal"
        closeOnDocumentClick
      >
        <V3PerformerPicker
          mode="edit"
          variant="modal"
          currentPerformer={pickedArtist
            ? { name: pickedArtistName(pickedArtist), act_type: currentPerformer?.act_type }
            : currentPerformer}
          teamId={team.id}
          defaultActType={currentPerformer?.act_type === 'headline' ? 'headline' : 'support'}
          description="Pick the artist for the copy. The deal terms and budget come with it."
          onSubmit={handlePick}
          onClose={closePicker}
        />
      </Popup>
    );
  }

  const artistName = pickedArtist
    ? pickedArtistName(pickedArtist)
    : (currentPerformer?.artistable?.name || offer.performer_name);
  const retargets = !!pickedArtist && !!currentPerformer;
  const isHold = confirm?.type === 'Hold';
  const eventNoun = isHold ? 'hold' : 'event';
  const keptCopyGetsOwnBudget = offer.v3_offer_pool?.kept_copies_get_own_budget ?? false;
  const keepsAsAlternative = !retargets && !!confirm && !!offer.copies_as_alternative && !keptCopyGetsOwnBudget;
  const keepsAsAct = !retargets && isHold && !offer.copies_as_alternative;
  const asksAboutKeep = keepsAsAlternative || keepsAsAct;
  const leavesEvent = !!confirm && !asksAboutKeep;
  const copy = (keepCalendarEvent) => onCopy({ keepCalendarEvent, artist: pickedArtist });

  const footer = (
    <div className="copy-offer-modal__footer">
      <Button variant="link-gray" onClick={onClose} disabled={isCopying}>Cancel</Button>
      {asksAboutKeep ? (
        <>
          <Button variant="secondary" onClick={() => copy(false)} disabled={isCopying}>
            Don&apos;t keep
          </Button>
          <Button variant="primary" onClick={() => copy(true)} disabled={isCopying}>
            {isCopying ? 'Copying…' : `Keep ${eventNoun}`}
          </Button>
        </>
      ) : (
        <Button variant="primary" onClick={() => copy(false)} disabled={isCopying}>
          {isCopying ? 'Copying…' : 'Copy offer'}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      closeOnBackdrop={!isCopying}
      closeOnEsc={!isCopying}
      showClose={!isCopying}
      icon={false}
      title="Copy offer"
      description={asksAboutKeep
        ? "The copy is a new draft with this offer's deal terms."
        : "The copy is a new draft with this offer's deal terms and budget."}
      footer={footer}
    >
      <div className="copy-offer-modal__artist">
        <div>
          <div className="copy-offer-modal__label">Artist</div>
          <div className="copy-offer-modal__name" data-testid="copy-offer-artist">
            {artistName || 'No artist'}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setPicking(true)}
          disabled={isCopying}
          aria-haspopup="dialog"
        >
          {artistName ? 'Change artist' : 'Choose artist'}
        </Button>
      </div>
      {keepsAsAlternative && (
        <p className="copy-offer-modal__note">
          {`This offer is linked to ${isHold ? 'a hold' : 'an event'}. Do you want to keep the copy on it as another offer for ${artistName}?`}
          {` It shares the ${eventNoun}'s budget with the original, so budget changes on either show on both. Only one of the artist's offers counts toward the ${eventNoun}: the accepted one, then one with a payment recorded on it, then the newest. Accepting one declines the others. Choose Don't keep for a separate copy of the budget.`}
        </p>
      )}
      {keepsAsAct && (
        <p className="copy-offer-modal__note">
          This offer is linked to a hold. Do you want to keep the hold link on the copy?
          {keptCopyGetsOwnBudget
            ? ' A copy kept on this hold stays out of its pool and starts with a new budget.'
            : " A copy kept on the hold joins its lineup and shares its budget with the original, so changes on either show on both and its guarantee counts like another act's. Choose Don't keep for a separate copy of the budget."}
        </p>
      )}
      {leavesEvent && (
        <p className="copy-offer-modal__note">
          {retargets && `A copy for a different artist won't be linked to this offer's ${eventNoun}.`}
          {!retargets && offer.copies_as_alternative && keptCopyGetsOwnBudget
            && `This ${eventNoun} pays its artists from a pool, so the copy won't be linked to it.`}
          {!retargets && !offer.copies_as_alternative && "The copy won't be linked to this offer's event."}
        </p>
      )}
    </Modal>
  );
};

CopyOfferModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isCopying: PropTypes.bool,
  offer: PropTypes.shape({
    performer_name: PropTypes.string,
    primary_performer: PropTypes.shape({
      artist_id: PropTypes.number,
      act_type: PropTypes.string,
      artistable: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    }),
    v3_offer_pool: PropTypes.shape({ kept_copies_get_own_budget: PropTypes.bool }),
    copies_as_alternative: PropTypes.bool,
  }).isRequired,
  confirm: PropTypes.shape({ type: PropTypes.string }),
  team: PropTypes.shape({ id: PropTypes.number }).isRequired,
  onClose: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
};

CopyOfferModal.defaultProps = {
  isCopying: false,
  confirm: null,
};

export default CopyOfferModal;
